import { mapOptionTypes } from 'app/center/asset_panel/asset_options/AssetOptionsPanel';
import { DashboardItemService } from 'app/overview/dashboard/dashboard_item.service';
import { IMS_TYPE__ASSET } from 'app/shared/collections/asset';
import { AssetCollection } from 'app/shared/collections/asset_collection.service';
import { IMS_TYPE__LOCATION_STAT } from 'app/shared/collections/location_stat';
import {
    ColorConfig,
    FIELD_SCHEMAS__TYPE_ASSET,
    FIELD_SCHEMAS__TYPE_FILE,
    FIELD_SCHEMAS__TYPE_IMAGE,
    FIELD_SCHEMAS__TYPE_IP_DEVICE,
    FIELD_SCHEMAS__TYPE_LON_LAT,
    FIELD_SCHEMAS__TYPE_NUMBER,
    FIELD_SCHEMAS__TYPE_POINT,
    FIELD_SCHEMAS__TYPE_RADIO,
    FIELD_SCHEMAS__TYPE_STRING,
    FIELD_SCHEMAS__TYPE_TENLOGTEN,
    FIELD_SCHEMAS__TYPE_TIMESTAMP,
    FIELD_SCHEMAS__TYPE_ZONE,
    FieldSchema,
    isForeignKeyField,
    STAT_DEFINITION__PARENT_TYPE_ASSET,
    STAT_DEFINITION__PARENT_TYPE_IP_DEVICE,
    STAT_DEFINITION__PARENT_TYPE_RADIO,
    STAT_DEFINITION__PARENT_TYPE_ZONE,
    StatDefinition,
    statDefinitionHasSubgroupField,
} from 'app/shared/collections/StatDefinition';
import { ImsTimeCollection } from 'app/shared/collections/time/ims_time_collection.factory';
import {
    GenericViewComponentSchema,
    View,
    ViewConnectionLineSchema,
    ViewIndicatorSchema,
    ViewTagBasedIndicatorSchema,
    ViewLabelSchema,
    ViewLineVisualizerSchema,
    ViewMapColorFollowRelationship,
    ViewPolygonVisualizerSchema,
    ViewPropagateLocationSchema,
    ViewVectorSchema,
} from 'app/shared/collections/view';
import { ZoneCollection } from 'app/shared/collections/zones/zone_collection.service';
import { ImsObjectUniqueColorRenderer } from 'app/shared/color/ImsObjectUniqueColorRenderer';
import { ImageUrlService, IndicatorIconType } from 'app/shared/image_url.service';
import { ImsItemTypeDetails, ImsItemTypeRegistry } from 'app/shared/ims_items/ImsItemTypeRegistry';
import { MapOption, MapOptionsService } from 'app/shared/map_options/MapOptionsService';
import {
    latLonOperators,
    numericalOperators,
    objectTypeOperators,
    stringOperators,
} from 'app/shared/operators';
import { ClockHandler } from 'app/shared/time/clock_handler.service';
import ColorRenderer, { BaseColorRenderer } from 'app/shared/utilities/color_renderer';
import { ImsObjectVisibilityService } from 'app/shared/visibility/ImsObjectVisibilityService';
import { IndicatorManager, ViewComponentModel } from 'app/views/accessories/IndicatorManager';
import { MultiIndicatorManager } from 'app/views/accessories/MultiIndicatorManager';
import { IMSAggregator } from 'app/views/aggregators/aggregator';
import { CountAggregator } from 'app/views/aggregators/count_aggregator';
import { FirstAggregator } from 'app/views/aggregators/first_aggregator';
import { TenLogMeanAggregator, TenLogSumAggregator } from 'app/views/aggregators/log_aggregators';
import { MaxAggregator } from 'app/views/aggregators/max_aggregator';
import { MeanAggregator } from 'app/views/aggregators/mean_aggregator';
import { MedianAggregator } from 'app/views/aggregators/median_aggregator';
import { MinAggregator } from 'app/views/aggregators/min_aggregator';
import { ModeAggregator } from 'app/views/aggregators/mode_aggregator';
import { MAP_OPTION_GROUP_INDICATORS, MapBottomChartOption } from 'app/views/BaseViewController';
import { EntityVisualiser } from 'app/views/render/entity_visualiser';
import { ILabelGetter } from 'app/views/value_getters/ILabelGetter';
import { IMSObjectNameLabelValueGetter } from 'app/views/value_getters/IMSObjectNameLabelValueGetter';
import { IMSValueGetter, StatFilterFnGenerator } from 'app/views/value_getters/value_getter';
import { AssetFollowRelationshipStatValueGetter } from 'app/views/view_types/generic/AssetFollowRelationshipStatValueGetter';
import { GenericAssetStatValueGetter } from 'app/views/view_types/generic/GenericAssetStatValueGetter';
import { GenericIPDeviceStatValueGetter } from 'app/views/view_types/generic/GenericIPDeviceStatValueGetter';
import { GenericRadioStatValueGetter } from 'app/views/view_types/generic/GenericRadioStatValueGetter';
import { IndicatorCollectionFactoryForViewConfigs } from 'app/views/view_types/IndicatorCollectionFactoryForViewConfigs';
import {
    Frequency,
    getRoundedFrequencyForChannel,
} from 'app/views/view_types/network/features/rf_planning/rf_utils';
import { IMSVisualiser } from 'app/views/visualiser';
import { SelectionService } from 'app/shared/selection/SelectionService';

const fieldExpressionPattern = RegExp('^.*stat.(.*).*$');

export interface VectorHeatMapContext {
    azimuthColumnName: string;
    elevationColumnName: string;
    magnitudeColumnName: string;
    magnitudeScale: number;
}

export function getIndicatorMapOptionName(
    config:
        | ViewIndicatorSchema
        | ViewTagBasedIndicatorSchema
        | ViewVectorSchema
        | ViewLabelSchema
        | ViewConnectionLineSchema
        | ViewPolygonVisualizerSchema
        | ViewPropagateLocationSchema
        | ViewLineVisualizerSchema,
): string {
    return config.display_name;
}

function getBlankBillboardImageUrl(imageUrlService: ImageUrlService): string {
    return imageUrlService.getUrl('indicator', 'base_icon', 'png');
}

export function createMapOptionForViewIndicatorConfig(
    config:
        | ViewIndicatorSchema
        | ViewTagBasedIndicatorSchema
        | ViewVectorSchema
        | ViewLabelSchema
        | ViewConnectionLineSchema
        | ViewPolygonVisualizerSchema
        | ViewPropagateLocationSchema
        | ViewLineVisualizerSchema,
): MapOption {
    return {
        label: config.display_name,
        groupAs: MAP_OPTION_GROUP_INDICATORS,
        description: config.description,
        fullWidth: true,
        type: mapOptionTypes.checkbox,
        value: false,
    };
}

export function resolveIconUrl(
    icon: IndicatorIconType | undefined,
    imageUrlService: ImageUrlService,
) {
    if (!icon) {
        return getBlankBillboardImageUrl(imageUrlService);
    }
    try {
        // this will throw an error if the icon doesn't exist in our lookup
        return imageUrlService.getUrl('indicator', icon, 'png');
    } catch (e) {
        console.error(e);
        console.error(`invalid indicator icon: ${icon}, using default`);
        return getBlankBillboardImageUrl(imageUrlService);
    }
}

export function createIndicatorManagerForView(
    view: View,
    imsItemTypeRegistry: ImsItemTypeRegistry,
    assetCollection: AssetCollection,
    clockHandler: ClockHandler,
    assetOptionsService: MapOptionsService,
    imageUrlService: ImageUrlService,
    selectionService: SelectionService,
): IndicatorManager {
    const factory = new IndicatorCollectionFactoryForViewConfigs(
        imsItemTypeRegistry,
        assetCollection,
        clockHandler,
        assetOptionsService,
        imageUrlService,
        selectionService,
    );

    const createFromConfig = (config, factoryMethod, description) => {
        let indicatorManager;

        try {
            indicatorManager = factoryMethod(config);
        } catch (e) {
            console.error(
                `Failed to create ${description} with the following config:\n\n${JSON.stringify(
                    config,
                    null,
                    4,
                )}\n\nSee error below...`,
            );
            console.error(e);
        }

        return indicatorManager;
    };

    //
    // map our view indicator components into actual indicator managers logging and filtering any
    // that are problematic (e.g. insane expressions etc)
    //

    function viewComponentsToManager<
        Schema extends GenericViewComponentSchema,
        Model extends ViewComponentModel,
    >(
        items: Array<Schema>,
        fn: (config: Schema) => IndicatorManager<Model>,
        name: string,
    ): Array<IndicatorManager<Model>> {
        return (items || []).map((config) => createFromConfig(config, fn, name));
    }

    const indicatorManagers = viewComponentsToManager(
        view.indicators,
        (config) => factory.createFromIndicatorConfig(config),
        'indicator',
    );

    const vectorIndicatorManagers = viewComponentsToManager(
        view.vectors,
        (config) => factory.createFromVectorConfig(config),
        'vector',
    );

    const labelIndicatorManagers = viewComponentsToManager(
        view.labels,
        (config) => factory.createFromLabelConfig(config),
        'label',
    );

    const connectionLineIndicatorManagers = viewComponentsToManager(
        view.connectionLines,
        (config) => factory.createFromConnectionLineConfig(config),
        'connection-line',
    );

    const polygonVisualizerIndicatorManagers = viewComponentsToManager(
        view.polygonVisualizers,
        (config) => factory.createFromPolygonVisualizerConfig(config),
        'polygon-visualizer',
    );

    const lineVisualizerIndicatorManagers = viewComponentsToManager(
        view.lineVisualizers,
        (config) => factory.createFromLineVisualizerConfig(config),
        'line-visualizer',
    );

    return new MultiIndicatorManager<ViewComponentModel>(
        [
            ...indicatorManagers,
            ...vectorIndicatorManagers,
            ...labelIndicatorManagers,
            ...connectionLineIndicatorManagers,
            ...polygonVisualizerIndicatorManagers,
            ...lineVisualizerIndicatorManagers,
        ].filter((x) => !!x),
    );
}

/**
 * Chooses and instantiates either a {@link GenericIPDeviceStatValueGetter} or a {@link GenericAssetStatValueGetter}
 * depending on the parent type of the stat.
 */
function createValueGetter(
    collection: ImsTimeCollection,
    key: string,
    statDefinition: StatDefinition,
    fieldSchema: FieldSchema,
    assetOptionsService: MapOptionsService,
    filterFnGenerator: StatFilterFnGenerator,
    imsItemTypeRegistry: ImsItemTypeRegistry,
    mapColorFollowRelationshipConfig: ViewMapColorFollowRelationship,
): IMSValueGetter {
    const isZoneStat = statDefinition.parent_type === STAT_DEFINITION__PARENT_TYPE_ZONE;
    const isAssetStat = statDefinition.parent_type === STAT_DEFINITION__PARENT_TYPE_ASSET;
    const isIPDeviceStat = statDefinition.parent_type === STAT_DEFINITION__PARENT_TYPE_IP_DEVICE;
    const isRadioStat = statDefinition.parent_type === STAT_DEFINITION__PARENT_TYPE_RADIO;

    // Dynamic stats wont have operators set, but we can detect them
    let operators = fieldSchema.operators;
    if (!operators) {
        if (fieldSchema.type === FIELD_SCHEMAS__TYPE_STRING) {
            operators = stringOperators;
        } else if (
            [FIELD_SCHEMAS__TYPE_LON_LAT, FIELD_SCHEMAS__TYPE_POINT].includes(fieldSchema.type)
        ) {
            operators = latLonOperators;
        } else if (isForeignKeyField(fieldSchema.type)) {
            operators = objectTypeOperators;
        } else if (
            ![FIELD_SCHEMAS__TYPE_FILE, FIELD_SCHEMAS__TYPE_IMAGE].includes(fieldSchema.type)
        ) {
            operators = numericalOperators;
        }
    }

    if (isZoneStat) {
        return new IMSValueGetter(
            collection,
            key,
            fieldSchema.units,
            filterFnGenerator,
            [],
            operators,
        );
    } else if (isAssetStat) {
        if (mapColorFollowRelationshipConfig) {
            // some complex relationship following logic
            //  vs an asset showing its own stats directly
            const relationshipCollection = imsItemTypeRegistry.getTypeDetails(
                mapColorFollowRelationshipConfig.relationship_table,
            ).collection as ImsTimeCollection;

            const assetCollection = imsItemTypeRegistry.getTypeDetails(IMS_TYPE__ASSET)
                .collection as AssetCollection;

            return new AssetFollowRelationshipStatValueGetter(
                operators,
                '',
                fieldSchema.units,
                key,
                fieldSchema.type,
                relationshipCollection,
                collection,
                assetCollection,
                mapColorFollowRelationshipConfig.relationship_field,
                mapColorFollowRelationshipConfig.src_asset_type_names,
            );
        } else {
            return new GenericAssetStatValueGetter(
                collection,
                key,
                fieldSchema.units,
                operators,
                filterFnGenerator,
            );
        }
    } else if (isIPDeviceStat) {
        return new GenericIPDeviceStatValueGetter(
            collection,
            key,
            fieldSchema.units,
            operators,
            filterFnGenerator,
        );
    } else if (isRadioStat) {
        return new GenericRadioStatValueGetter(
            collection,
            key,
            fieldSchema.units,
            operators,
            filterFnGenerator,
        );
    }

    throw Error(
        `Cannot create ValueGetter for ${statDefinition.name}.${key} (parent_type=${statDefinition.parent_type})`,
    );
}

function getEntityAndHeatMapAggregators(
    statDefinition: StatDefinition,
    fieldSchema: FieldSchema,
    mapColorFollowRelationshipConfig: ViewMapColorFollowRelationship,
): { entityAggregators: Array<IMSAggregator>; heatMapAggregators: Array<IMSAggregator> } {
    const isAssetStatWithSubgroup =
        statDefinition.parent_type === STAT_DEFINITION__PARENT_TYPE_ASSET &&
        statDefinitionHasSubgroupField(statDefinition);
    const isAssetStatWithRelationshipConfig =
        statDefinition.parent_type === STAT_DEFINITION__PARENT_TYPE_ASSET &&
        !!mapColorFollowRelationshipConfig;
    const isIPDeviceStat = statDefinition.parent_type === STAT_DEFINITION__PARENT_TYPE_IP_DEVICE;
    const isRadioStat = statDefinition.parent_type === STAT_DEFINITION__PARENT_TYPE_RADIO;

    // if there's a chance we'll get multiple stats at a single point in time;
    //  then we want to use a different set of aggregations
    const willGetMultipleStatsAtAPointInTime =
        isRadioStat ||
        isIPDeviceStat ||
        isAssetStatWithSubgroup ||
        isAssetStatWithRelationshipConfig;

    let entityAggregators;
    let heatMapAggregators;

    if (fieldSchema.type === FIELD_SCHEMAS__TYPE_IMAGE) {
        entityAggregators = [new CountAggregator()];
        heatMapAggregators = [new CountAggregator()];
    } else if (fieldSchema.type === FIELD_SCHEMAS__TYPE_FILE) {
        entityAggregators = [new CountAggregator()];
        heatMapAggregators = [new CountAggregator()];
    } else if (fieldSchema.type === FIELD_SCHEMAS__TYPE_TIMESTAMP) {
        entityAggregators = [new CountAggregator()];
        heatMapAggregators = [new CountAggregator()];
    } else if (
        [FIELD_SCHEMAS__TYPE_LON_LAT, FIELD_SCHEMAS__TYPE_POINT].includes(fieldSchema.type)
    ) {
        entityAggregators = [];
        heatMapAggregators = [new CountAggregator()];
    } else if (fieldSchema.type === FIELD_SCHEMAS__TYPE_STRING) {
        if (willGetMultipleStatsAtAPointInTime) {
            entityAggregators = [new ModeAggregator(), new FirstAggregator()];
        } else {
            entityAggregators = [new FirstAggregator()];
        }
        heatMapAggregators = [new ModeAggregator()];
    } else if (fieldSchema.type === FIELD_SCHEMAS__TYPE_NUMBER) {
        if (willGetMultipleStatsAtAPointInTime) {
            entityAggregators = [
                new MeanAggregator(),
                new MedianAggregator(),
                new ModeAggregator(),
                new MaxAggregator(),
                new MinAggregator(),
                new CountAggregator(),
                new FirstAggregator(),
            ];
        } else {
            entityAggregators = [new FirstAggregator()];
        }
        heatMapAggregators = [
            new MeanAggregator(),
            new MedianAggregator(),
            new ModeAggregator(),
            new MaxAggregator(),
            new MinAggregator(),
            new CountAggregator(),
        ];
    } else if (fieldSchema.type === FIELD_SCHEMAS__TYPE_TENLOGTEN) {
        if (willGetMultipleStatsAtAPointInTime) {
            entityAggregators = [
                new MeanAggregator(),
                new MedianAggregator(),
                new ModeAggregator(),
                new MaxAggregator(),
                new MinAggregator(),
                new CountAggregator(),
                new TenLogMeanAggregator(),
                new TenLogSumAggregator(),
                new FirstAggregator(),
            ];
        } else {
            entityAggregators = [new FirstAggregator()];
        }
        heatMapAggregators = [
            new MeanAggregator(),
            new MedianAggregator(),
            new ModeAggregator(),
            new MaxAggregator(),
            new MinAggregator(),
            new CountAggregator(),
            new TenLogMeanAggregator(),
            new TenLogSumAggregator(),
        ];
    } else if (
        [
            FIELD_SCHEMAS__TYPE_ASSET,
            FIELD_SCHEMAS__TYPE_IP_DEVICE,
            FIELD_SCHEMAS__TYPE_RADIO,
            FIELD_SCHEMAS__TYPE_ZONE,
        ].includes(fieldSchema.type)
    ) {
        // foreign key aggregators
        if (willGetMultipleStatsAtAPointInTime) {
            entityAggregators = [new ModeAggregator(), new FirstAggregator()];
        } else {
            entityAggregators = [new FirstAggregator()];
        }
        heatMapAggregators = [new ModeAggregator()];
    } else {
        entityAggregators = [];
        heatMapAggregators = [];
    }

    return {
        entityAggregators,
        heatMapAggregators,
    };
}

export function createImsVisualiser(
    imsStatType: ImsItemTypeDetails,
    fieldSchema: FieldSchema,
    fieldName: string,
    assetCollection: AssetCollection,
    zoneCollection: ZoneCollection,
    imageUrlService: ImageUrlService,
    assetOptionsService: MapOptionsService,
    zoneOptionsService: MapOptionsService,
    filterFnGenerator: StatFilterFnGenerator,
    imsObjectVisibilityService: ImsObjectVisibilityService,
    vectorHeatMapContext: VectorHeatMapContext | null,
    imsItemTypeRegistry: ImsItemTypeRegistry,
    mapColorFollowRelationshipConfig: ViewMapColorFollowRelationship,
): IMSVisualiser {
    const colorConfig: ColorConfig = fieldSchema.color_config || undefined;

    // A valueGetter is created based on the stat parent type
    const valueGetter = createValueGetter(
        imsStatType.collection,
        fieldName,
        imsStatType.statDefinition,
        fieldSchema,
        assetOptionsService,
        filterFnGenerator,
        imsItemTypeRegistry,
        mapColorFollowRelationshipConfig,
    );

    let colorRenderer: BaseColorRenderer | undefined = colorConfig
        ? new ColorRenderer(colorConfig)
        : undefined;
    let labelValueGetter: ILabelGetter = valueGetter;

    if (fieldSchema.type === FIELD_SCHEMAS__TYPE_ASSET) {
        colorRenderer = new ImsObjectUniqueColorRenderer(
            assetCollection,
            undefined,
            undefined,
            'Default',
        );
        labelValueGetter = new IMSObjectNameLabelValueGetter(valueGetter, assetCollection);
    } else if (fieldSchema.type === FIELD_SCHEMAS__TYPE_ZONE) {
        colorRenderer = new ImsObjectUniqueColorRenderer(
            zoneCollection,
            undefined,
            undefined,
            'default',
        );
        labelValueGetter = new IMSObjectNameLabelValueGetter(valueGetter, zoneCollection);
    }

    const { entityAggregators, heatMapAggregators } = getEntityAndHeatMapAggregators(
        imsStatType.statDefinition,
        fieldSchema,
        mapColorFollowRelationshipConfig,
    );

    const entityVisualiser = new EntityVisualiser(
        colorRenderer,
        valueGetter,
        imageUrlService,
        assetOptionsService,
        zoneOptionsService,
        imsObjectVisibilityService,
        imsItemTypeRegistry,
        entityAggregators,
        labelValueGetter,
    );

    // Need to combine the table name with the column name to avoid name collisions.
    // e.g. estimated movement stat columns collide with estimated movement stat by zone columns.
    const logicalName = `${imsStatType.statDefinition.name}_${fieldName}`;
    const displayName = fieldSchema.display_name ? fieldSchema.display_name : logicalName;

    return new IMSVisualiser(
        vectorHeatMapContext ? `${logicalName}_vector` : logicalName,
        imsStatType.statDefinition.name,
        vectorHeatMapContext ? `${displayName} - Vector` : displayName,
        colorRenderer,
        valueGetter,
        entityVisualiser,
        heatMapAggregators,
        imsStatType.statDefinition.parent_type,
        imsStatType.displayName,
        fieldSchema.type,
        vectorHeatMapContext,
    );
}

const DEFAULT_BOTTOM_CHART_AGGREGATION = 'avg';
const FALLBACK_BOTTOM_CHART_AGGREGATION = 'max';

export function getBottomChartOptionsForStatType(
    statDefinition: StatDefinition,
    imsStatName: string,
    dashboardItemService: DashboardItemService,
): Array<MapBottomChartOption> {
    const dataSource = imsStatName;
    const groupBy = statDefinition.parent_type;
    const dataSourceFields = dashboardItemService.getFieldsForDataSource(dataSource);
    return dataSourceFields.map((dataField) => {
        const sourceName = dashboardItemService.getDataSourceName(dataSource);
        const fieldName = dashboardItemService.getDataFieldName(dataField);
        const possibleAggregations = dashboardItemService.getAggregationsForField(dataField);
        const aggregationType = possibleAggregations.includes(DEFAULT_BOTTOM_CHART_AGGREGATION)
            ? DEFAULT_BOTTOM_CHART_AGGREGATION
            : FALLBACK_BOTTOM_CHART_AGGREGATION;
        return {
            name: `${sourceName} - ${fieldName}`,
            dataSource,
            dataField,
            groupBy,
            aggregationType,
        };
    });
}

export function getItemTypeDetailsFromViewTables(
    view: View,
    imsItemTypeRegistry: ImsItemTypeRegistry,
): { [type: string]: ImsItemTypeDetails } {
    const itemTypeDetails = {};
    for (const statType in view.tables) {
        const details = imsItemTypeRegistry.getTypeDetails(statType);
        if (details) {
            itemTypeDetails[statType] = details;
        }
    }

    // location stats we always want in the view tables definition even though it doesn' appear in there explicitly
    itemTypeDetails[IMS_TYPE__LOCATION_STAT] =
        imsItemTypeRegistry.getTypeDetails(IMS_TYPE__LOCATION_STAT);

    return itemTypeDetails;
}

export function getCollectionNameFromItemTypeDetails(itemTypeDetails: ImsItemTypeDetails): string {
    return itemTypeDetails.collection.constructor.name;
}

export function getVectorHeatMapContext(
    currentView: View,
    imsVisualiser: IMSVisualiser,
    columnName: string,
): VectorHeatMapContext | null {
    let azimuthColumnName: string | undefined;
    let elevationColumnName: string | undefined;

    // TODO: this is a bit fuzzy + doesn't cater for multiple vectors for same table and column
    //  don't worry- there's a ticket: https://app.clickup.com/t/75jr8j
    // iterate the components in this view to find any vector details that apply to this
    // table and column
    currentView?.components?.forEach((component: ViewVectorSchema) => {
        const headingExpr = component.heading_expr as string;
        const pitchExpr = component.pitch_expr as string;

        if (
            component.type !== 'vector' ||
            component.table !== imsVisualiser.tableName ||
            !component.length_expr.toString().includes(columnName) ||
            !(headingExpr && pitchExpr)
        ) {
            return;
        }

        const azimuthColumnNameMatches = (component.heading_expr as string).match(
            fieldExpressionPattern,
        );

        const elevationColumnNameMatches = (component.pitch_expr as string).match(
            fieldExpressionPattern,
        );

        if (
            !(azimuthColumnNameMatches && azimuthColumnNameMatches.length == 2) ||
            !(elevationColumnNameMatches && elevationColumnNameMatches.length == 2)
        ) {
            return;
        }

        azimuthColumnName = azimuthColumnNameMatches[1];
        elevationColumnName = elevationColumnNameMatches[1];
    });

    const magnitudeScale = 1.0; // TODO: support in ViewVectorSchema

    if (!(azimuthColumnName && elevationColumnName && columnName && magnitudeScale)) {
        return null;
    }

    return {
        azimuthColumnName: azimuthColumnName,
        elevationColumnName: elevationColumnName,
        magnitudeColumnName: columnName,
        magnitudeScale: magnitudeScale,
    } as VectorHeatMapContext;
}

(window as any).getChannelAndPowerForIpDeviceStat = (ipDeviceStats) => {
    if (!ipDeviceStats || ipDeviceStats.length === 0) {
        return null;
    }

    ipDeviceStats
        .filter((s) => s.radioStats && s.radioStats.length > 0)
        .sort((a, b) => a.ipDeviceId - b.ipDeviceId);

    if (!ipDeviceStats || ipDeviceStats.length === 0) {
        return null;
    }
    for (let i = 0; i < ipDeviceStats.length; i++) {
        const stat = ipDeviceStats[i];
        const result = stat.radioStats
            .sort((a, b) => a.radioId - b.radioId)
            .find((r) => getRoundedFrequencyForChannel(r.channel) === Frequency._2_4_GHZ);

        if (result) {
            return `${result.channel}/${result.tx_power}dBm`;
        }
    }

    return null;
};
